import React, { useEffect, useRef, useState } from 'react'
import TicketRowDate from './TicketRowDate.jsx'
import PhoneFilterSheet from './PhoneFilterSheet.jsx'
import { ticketTopicLabel } from '../../../../shared/ticketTopics.js'
import PhoneMenuPopover from './PhoneMenuPopover.jsx'
import PresenceChip from './PresenceChip.jsx'
import { useLongPress } from './hooks.js'

/**
 * Inbox view — the ticket list, plus on phone (breakpoint==='phone')
 * the inbox header (title + search/filter/overflow icons), inline search
 * input, and horizontal tab strip.
 *
 * On desktop / tablet the chrome (search box, tab strip, bulk actions)
 * lives in the orchestrator above the layout — TicketsInbox just renders
 * the `<aside ticketListColumn>` ticket list. On phone the orchestrator
 * hides that chrome and the inbox owns the whole vertical surface.
 *
 * State stays in the orchestrator; this component is dumb.
 */

// D11 — predicates for the phone filter sheet's three chip groups.
// Applied client-side on top of the tab/search-filtered list returned
// by loadTickets. Empty selections in a group = no filtering for that
// group; selections OR together within a group, groups AND together.
export function applyPhoneFilter(tickets, filter, currentUserId) {
  const { assignees = [], channels = [], types = [] } = filter || {}
  if (!assignees.length && !channels.length && !types.length) return tickets

  return tickets.filter(t => {
    if (assignees.length) {
      const ok = assignees.some(a => {
        if (a === 'unassigned') return !t.assigned_to
        if (a === 'me') return t.assigned_to === currentUserId
        return false
      })
      if (!ok) return false
    }
    if (channels.length) {
      if (!channels.includes(t.channel || 'email')) return false
    }
    if (types.length) {
      const ok = types.some(ty => {
        if (ty === 'returns') return t.template_category === 'returns'
        return false
      })
      if (!ok) return false
    }
    return true
  })
}

const PHONE_TABS = [
  { id: 'needs_reply', label: 'Needs Reply', countKey: 'needs_reply' },
  { id: 'offers', label: 'Offers', countKey: 'offers' },
  { id: 'waiting', label: 'Waiting', countKey: 'waiting' },
  { id: 'resolved', label: 'Resolved', countKey: 'resolved' },
  { id: 'service', label: 'Service', countKey: 'service' },
  { id: 'spam', label: 'Spam', countKey: 'spam' }
]

function PhoneInboxHeader({ onOpenSearch, onOpenFilters, onOpenMore, moreRef }) {
  return (
    <div className="inbox-header">
      <h1>Tickets</h1>
      <button className="icon-btn" aria-label="Search tickets" onClick={onOpenSearch}>🔍</button>
      <button className="icon-btn" aria-label="Filter tickets" onClick={onOpenFilters}>⚙</button>
      <button ref={moreRef} className="icon-btn" aria-label="More actions" onClick={onOpenMore}>⋮</button>
    </div>
  )
}

function PhoneTabStrip({ activeTab, setActiveTab, counts }) {
  return (
    <div className="tab-strip" role="tablist" aria-label="Ticket categories">
      {PHONE_TABS.map(t => {
        const count = counts?.[t.countKey] || 0
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={activeTab === t.id}
            className={`tab ${activeTab === t.id ? 'tab--active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label} ({count})
          </button>
        )
      })}
    </div>
  )
}

function PhoneTicketRow({
  ticket, viewers, selectedTicket, selectedIds, bulkMode,
  getStateDisplay, getTicketType,
  selectTicket, toggleSelection, onEnterBulk
}) {
  const isMuted = ['spam', 'service', 'offers'].includes(ticket.status)
  const stateDisplay = getStateDisplay(ticket.status)
  const ticketType = getTicketType(ticket)
  const isSelected = selectedTicket?.id === ticket.id
  const isPicked = selectedIds.has(ticket.id)
  const longPress = useLongPress(() => onEnterBulk(ticket.id))

  const handleClick = () => {
    if (longPress.didLongPress()) return // swallow tap-through after long press
    if (bulkMode) toggleSelection(ticket.id)
    else selectTicket(ticket)
  }

  return (
    <div
      className={`ticket-row${isSelected ? ' ticket-row--selected' : ''}${isPicked ? ' ticket-row--bulk-selected' : ''}${isMuted ? ' ticket-row--muted' : ''}`}
      onClick={handleClick}
      onPointerDown={longPress.onPointerDown}
      onPointerUp={longPress.onPointerUp}
      onPointerMove={longPress.onPointerMove}
      onPointerCancel={longPress.onPointerCancel}
      onPointerLeave={longPress.onPointerLeave}
    >
      <div className={`pbar pbar--${ticket.priority || 'normal'}`} />
      <div className="body">
        <div className="sub">
          {ticket.subject || '(No subject)'}
          {ticket.channel === 'ebay' && <span style={{ marginLeft: 6, padding: '1px 5px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: '#0654ba', color: 'white' }}>eBay</span>}
          {ticket.channel === 'reverb' && <span style={{ marginLeft: 6, padding: '1px 5px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: '#6b21a8', color: 'white' }}>Reverb</span>}
        </div>
        <div className="meta">
          <PresenceChip viewers={viewers} />
          {ticket.customer_name_extracted || ticket.from_name || ticket.from_email || ticket.customer_email}
          {ticketType && <> · {ticketType}</>}
          {ticket.topic && <> · {ticketTopicLabel(ticket.topic)}</>}
          {' · '}
          <span className={stateDisplay.className}>{stateDisplay.label}</span>
          {' · '}
          <TicketRowDate ticket={ticket} className="ticket-row__phone-date" />
        </div>
        <div className="snippet">{ticket.snippet}</div>
      </div>
    </div>
  )
}

function LoadMore({ hasMore, loadingMore, onLoadMore }) {
  if (!hasMore) return null
  return (
    <div style={{ padding: 'var(--space-3)', textAlign: 'center' }}>
      <button className="btn btn-secondary btn-sm" onClick={onLoadMore} disabled={loadingMore}>
        {loadingMore ? 'Loading…' : 'Load more'}
      </button>
    </div>
  )
}

function TicketsInbox({
  // Layout
  breakpoint, mobileV2 = false,
  // Visibility / sizing (desktop)
  isMobile, mobileView, selectedTicket, leftPanelWidth,
  // List data
  tickets, loading, activeTab, setActiveTab, counts,
  // Paging: the list holds the first N tickets of the tab; onLoadMore appends the next page
  hasMore = false, loadingMore = false, onLoadMore,
  // Search (phone only — desktop search lives in pageHead)
  search, setSearch,
  // Bulk-edit
  bulkMode, setBulkMode, selectedIds, setSelectedIds, selectAll, toggleSelection,
  bulkUpdateStatus,
  // Per-row helpers
  getStateDisplay, getTicketType, getUserName, getUserInitials,
  // Selection + customer modal
  selectTicket, setCustomerEmail,
  // Assignment
  user, handleAssign,
  // Presence — who else is on each ticket, keyed by ticket id
  viewersByTicket = {},
  // Phone header callbacks (each menu item gets its own discrete callback
  // so the orchestrator owns side-effects; the inbox just opens the menu)
  onOpenFilters, onSync, onOpenEmailFilters, onOpenSignature
}) {
  // Only switch to the phone visual treatment when the mobile-v2 flag is
  // on. Without it we keep the desktop aside (the legacy mobile behaviour
  // is handled via isMobile + display:none gating below).
  const isPhone = breakpoint === 'phone' && mobileV2
  const [phoneSearchOpen, setPhoneSearchOpen] = useState(!!search)
  const [filterSheetOpen, setFilterSheetOpen] = useState(false)
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false)
  const moreBtnRef = useRef(null)
  // Phone filter sheet state. Selections filter the rendered list
  // client-side (loadTickets already returns the active-tab subset).
  const [phoneFilter, setPhoneFilter] = useState({ assignees: [], channels: [], types: [] })
  // M3 — clear the phone filter when the operator switches tab.
  // Otherwise an "eBay" chip from the Needs Reply tab silently hides
  // most of the list when they look at Resolved next.
  useEffect(() => {
    setPhoneFilter({ assignees: [], channels: [], types: [] })
  }, [activeTab])

  // Apply client-side phone filter on top of the tab/search-filtered
  // list. The Apply button on the sheet shows the live count; closing
  // the sheet keeps the filter active.
  const displayedTickets = isPhone ? applyPhoneFilter(tickets, phoneFilter, user?.user_id) : tickets

  if (isPhone) {
    return (
      <div className="inbox inbox--phone">
        <PhoneInboxHeader
          onOpenSearch={() => setPhoneSearchOpen(v => !v)}
          onOpenFilters={() => { setFilterSheetOpen(true); onOpenFilters?.() }}
          onOpenMore={() => setOverflowMenuOpen(true)}
          moreRef={moreBtnRef}
        />
        <PhoneMenuPopover
          open={overflowMenuOpen}
          onClose={() => setOverflowMenuOpen(false)}
          anchorRef={moreBtnRef}
          items={[
            { label: 'Sync emails', onClick: () => onSync?.() },
            { label: 'Email filters', onClick: () => onOpenEmailFilters?.() },
            { label: 'Signature', onClick: () => onOpenSignature?.() }
          ]}
        />
        <PhoneFilterSheet
          open={filterSheetOpen}
          onClose={() => setFilterSheetOpen(false)}
          onApply={() => setFilterSheetOpen(false)}
          resultCount={displayedTickets.length}
          value={phoneFilter}
          setValue={setPhoneFilter}
          options={{
            assignees: [{ value: 'unassigned', label: 'Unassigned' }, { value: 'me', label: 'Assigned to me' }],
            channels: [{ value: 'email', label: 'Email' }, { value: 'ebay', label: 'eBay' }, { value: 'reverb', label: 'Reverb' }],
            types: [{ value: 'returns', label: 'Returns' }]
          }}
        />
        {phoneSearchOpen && (
          <div className="search-row">
            <input
              type="search"
              autoFocus
              placeholder="Search tickets"
              value={search || ''}
              onChange={e => setSearch(e.target.value)}
              inputMode="search"
            />
          </div>
        )}
        <PhoneTabStrip activeTab={activeTab} setActiveTab={setActiveTab} counts={counts} />
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="loading-spinner" />
          </div>
        ) : displayedTickets.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
            <p>No {activeTab} tickets{tickets.length > 0 ? ' match the active filter' : ''}</p>
          </div>
        ) : (
          <div>
            {displayedTickets.map(t => (
              <PhoneTicketRow
                key={t.id}
                ticket={t}
                viewers={viewersByTicket[t.id]}
                selectedTicket={selectedTicket}
                selectedIds={selectedIds}
                bulkMode={bulkMode}
                getStateDisplay={getStateDisplay}
                getTicketType={getTicketType}
                selectTicket={selectTicket}
                toggleSelection={toggleSelection}
                onEnterBulk={(id) => {
                  setBulkMode?.(true)
                  setSelectedIds?.(new Set([id]))
                }}
              />
            ))}
            <LoadMore hasMore={hasMore} loadingMore={loadingMore} onLoadMore={onLoadMore} />
          </div>
        )}
        {bulkMode && (
          <div className="bulk-bar" role="region" aria-label="Bulk actions">
            <span className="count">{selectedIds.size} selected</span>
            {/* M4/M5: Select-all picks the *visible* (post-filter) tickets,
                not the full underlying tickets array. If the operator has
                a filter on, "Select all" matches what they see. */}
            <button onClick={() => setSelectedIds?.(new Set(displayedTickets.map(t => t.id)))}>Select all</button>
            <button onClick={() => setSelectedIds?.(new Set())}>Clear</button>
            <button onClick={() => bulkUpdateStatus?.('resolved')}>Resolve</button>
            <button onClick={() => bulkUpdateStatus?.('waiting_customer')}>Wait cust.</button>
            <button onClick={() => bulkUpdateStatus?.('waiting_third_party')}>Wait 3p</button>
            <button onClick={() => bulkUpdateStatus?.('service')}>Service</button>
            <button onClick={() => bulkUpdateStatus?.('spam')}>Spam</button>
            <button onClick={() => bulkUpdateStatus?.('new')}>Reopen</button>
            <button onClick={() => { setBulkMode?.(false); setSelectedIds?.(new Set()) }}>Cancel</button>
          </div>
        )}
      </div>
    )
  }

  // Desktop / tablet path — original aside-based list.
  return (
    <aside
      className="card ticketListColumn"
      style={{
        padding: 0,
        width: isMobile ? '100%' : (selectedTicket ? leftPanelWidth : '100%'),
        display: isMobile && selectedTicket && mobileView !== 'list' ? 'none' : 'flex'
      }}
    >
      {bulkMode && tickets.length > 0 && (
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,.06)', background: 'var(--bg)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={selectedIds.size === tickets.length && tickets.length > 0}
              onChange={selectAll}
            />
            Select All
          </label>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div className="loading-spinner" />
        </div>
      ) : tickets.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
          <p>No {activeTab} tickets</p>
        </div>
      ) : (
        <div>
          {tickets.map(ticket => {
            const isMuted = ['spam', 'service', 'offers'].includes(ticket.status)
            const stateDisplay = getStateDisplay(ticket.status)
            const ticketType = getTicketType(ticket)
            return (
              <div
                key={ticket.id}
                className={`ticket-row${selectedTicket?.id === ticket.id ? ' ticket-row--selected' : ''}${selectedIds.has(ticket.id) ? ' ticket-row--bulk-selected' : ''}${isMuted ? ' ticket-row--muted' : ''}`}
                onClick={() => selectTicket(ticket)}
              >
                {bulkMode && (
                  <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 12 }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(ticket.id)}
                      onChange={() => toggleSelection(ticket.id)}
                      onClick={e => e.stopPropagation()}
                    />
                  </div>
                )}
                {/* Priority bar */}
                <div className={`ticket-row__priority-bar ticket-row__priority-bar--${ticket.priority || 'normal'}`} />

                {/* Body */}
                <div className="ticket-row__body">
                  <div className={`ticket-row__subject${!ticket.viewed_at ? ' ticket-row__subject--unread' : ''}`}>
                    {ticket.subject || '(No subject)'}
                    {ticket.channel === 'ebay' && (
                      <span style={{ marginLeft: 6, padding: '1px 5px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: '#0654ba', color: 'white', verticalAlign: 'middle' }}>eBay</span>
                    )}
                    {ticket.channel === 'reverb' && (
                      <span style={{ marginLeft: 6, padding: '1px 5px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: '#6b21a8', color: 'white', verticalAlign: 'middle' }}>Reverb</span>
                    )}
                  </div>
                  <div className="ticket-row__sender">
                    <a
                      onClick={(e) => { e.stopPropagation(); setCustomerEmail(ticket.from_email || ticket.customer_email); }}
                      title="View customer history"
                    >
                      {ticket.customer_name_extracted || ticket.from_name || ticket.from_email || ticket.customer_email}
                    </a>
                    {ticket.customer_email && (
                      <span style={{ fontSize: 11, color: '#6B7280', marginLeft: 6 }} title={`Via ${ticket.from_email}`}>
                        (via contact form)
                      </span>
                    )}
                  </div>
                  <div className="ticket-row__meta">
                    {ticketType && (
                      <>
                        <span className="ticket-row__type">{ticketType}</span>
                        <span className="ticket-row__meta-dot" />
                      </>
                    )}
                    <span className={`ticket-row__state ${stateDisplay.className}`}>
                      {stateDisplay.label}
                    </span>
                    {ticket.topic && (
                      <>
                        <span className="ticket-row__meta-dot" />
                        <span className="ticket-row__type">{ticketTopicLabel(ticket.topic)}</span>
                      </>
                    )}
                  </div>
                  <div className="ticket-row__snippet">
                    {ticket.snippet}
                  </div>
                </div>

                {/* Right column */}
                <div className="ticket-row__right">
                  <PresenceChip viewers={viewersByTicket[ticket.id]} />
                  {ticket.assigned_to ? (
                    <span
                      title={getUserName(ticket.assigned_to)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 24, height: 24, borderRadius: '50%',
                        background: 'var(--color-primary, #2563eb)', color: '#fff',
                        fontSize: 10, fontWeight: 600, flexShrink: 0
                      }}
                    >
                      {getUserInitials(ticket.assigned_to)}
                    </span>
                  ) : (
                    <span
                      title="Assign to me"
                      onClick={e => { e.stopPropagation(); handleAssign(ticket.id, user?.user_id) }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 24, height: 24, borderRadius: '50%',
                        border: '1.5px dashed var(--color-border, #d1d5db)', color: 'var(--color-text-muted, #9ca3af)',
                        fontSize: 10, cursor: 'pointer', flexShrink: 0
                      }}
                    >
                      +
                    </span>
                  )}
                  {!ticket.viewed_at && <div className="ticket-row__unread-dot" title="Not yet viewed" />}
                  <TicketRowDate ticket={ticket} />
                  <div className="ticket-row__badges">
                    {ticket.deal_id && (
                      <span className="badge badge-success" style={{ fontSize: 10 }}>Linked</span>
                    )}
                    {ticket.order_id && (
                      <span className="badge badge-warning" style={{ fontSize: 10 }}>#{ticket.order_id}</span>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
          <LoadMore hasMore={hasMore} loadingMore={loadingMore} onLoadMore={onLoadMore} />
        </div>
      )}
    </aside>
  )
}

export default TicketsInbox
